import { Core } from "@faire/design-tokens";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getUserToken } from "@faire/help-center-utils/serialized-data/getUser";
import { getSettingHelpCenterTicketPeteEventsFromBE } from "@faire/help-center-utils/settings/getSettingHelpCenterTicketPeteEventsFromBE";
import { getSettingPhoneCallbackAttachment } from "@faire/help-center-utils/settings/getSettingPhoneCallbackAttachment";
import { getSettingPhoneSLA } from "@faire/help-center-utils/settings/getSettingPhoneSLA";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { PhoneInput } from "@faire/web--source/slate-labs/Input/PhoneInput";
import { Button } from "@faire/web--source/slate/Button";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useMutation as usePhoneCallbackMutation } from "@faire/web-api--source/api/help-center/phone/callback_request/post-hooks";
import { trackCxFoundationsEmailUsPageView } from "@faire/web-api--source/events/cxFoundations/view/emailUsPage";
import { trackHelpCenterEditPhoneNumberClick } from "@faire/web-api--source/events/helpCenter/click/editPhoneNumber";
import { trackHelpCenterAllTypesOfTicketCreationSubmit } from "@faire/web-api--source/events/helpCenter/submit/allTypesOfTicketCreation";
import { trackHelpCenterPhoneSupportSubmit } from "@faire/web-api--source/events/helpCenter/submit/phoneSupport";
import { trackHelpCenterCallRequestView } from "@faire/web-api--source/events/helpCenter/view/callRequest";
import { StringContextType } from "@faire/web-api--source/faire/translator/StringContextType";
import { IHCPhoneCallbackRequest } from "@faire/web-api--source/indigofair/helpcenter/IHCPhoneCallbackRequest";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { BackButton } from "../../components/BackButton";
import { Strategy, TicketType } from "../../consts";
import { useEditSettingsLink } from "../../hooks/useEditSettingsLink";
import {
  ICallRequestForm,
  useInitializeCallRequestField,
} from "../../hooks/useInitializeCallRequestField";
import { useIssueType } from "../../hooks/useIssueType";
import { useNavigation } from "../../hooks/useNavigation";
import { useScrollToTopOnLoadEffect } from "../../hooks/useScrollToTopEffect";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { useFormatTime } from "../Answer/hooks/useFormatTime";
import { LimitedWidthWrapper } from "../components/LimitedWidthWrapper";
import { AttachmentFormField } from "../Email/AttachmentFormField";

import { getCallRequestTicketPayload } from "./getCallRequestTicketPayload";
import { PhoneDescription } from "./PhoneDescription";
import { PhoneNumberErrorMessage } from "./PhoneNumberErrorMessage";

export const Phone: React.FC = () => {
  useTrackPageViewEffect(trackCxFoundationsEmailUsPageView);
  useScrollToTopOnLoadEffect(10);
  const { strictLocalize } = useStrictLocalization();
  const { issueType } = useIssueType();
  const [attachment, setAttachment] = useState<File | undefined>(undefined);
  const retailerToken = getRetailerToken() ?? "";
  const brandToken = getBrandToken() ?? "";
  const userToken = getUserToken() ?? "";
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasPhoneInputError, setHasPhoneInputError] = useState<boolean>(false);
  const [details, setDetails] = useState<string>("");
  const { mutate: mutatePhoneCallback } = usePhoneCallbackMutation();
  const { callRequestInitial } = useInitializeCallRequestField();
  const { phone: phoneNumber } = callRequestInitial;
  const { goToNextStep } = useNavigation();
  const editSettingsLink = useEditSettingsLink();
  const waitTime = getSettingPhoneSLA();
  const formattedWaitTime = useFormatTime(waitTime);

  useEffect(() => {
    trackHelpCenterCallRequestView(retailerToken, brandToken);
  }, [brandToken, retailerToken]);

  useEffect(() => {
    if (!phoneNumber) {
      setHasPhoneInputError(true);
    } else {
      setHasPhoneInputError(false);
    }
  }, [phoneNumber]);

  const handleEditSettingsClick = () => {
    trackHelpCenterEditPhoneNumberClick(
      getRetailerToken() ?? "",
      getBrandToken() ?? ""
    );
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (hasPhoneInputError) {
        setIsLoading(false);
        setErrorMessage(
          strictLocalize({
            defaultMessage:
              "Please add a phone number in your account settings.",
          })
        );
        return;
      }
      const callRequestPayload = {
        ...callRequestInitial,
        phone: phoneNumber,
        details,
      } as ICallRequestForm;
      const { phoneTicketPayload } = getCallRequestTicketPayload({
        values: callRequestPayload,
        issueType,
      });
      const {
        brand_order_token,
        phone_number,
        email,
        subject,
        description,
        issue_type,
        platform,
        os_version,
        app_version,
        tags,
      } = phoneTicketPayload;

      // Try use Phone callback API prototype
      mutatePhoneCallback([
        IHCPhoneCallbackRequest.build({
          phone_number: phoneNumber,
          customer_token: retailerToken ?? brandToken ?? userToken ?? "",
          brand_order_token,
          issue_type,
          platform,
          os_version,
          app_version,
          tags: tags.filter((tag) => tag !== "").join(", "),
          description,
        }),
      ]);

      trackHelpCenterPhoneSupportSubmit(
        retailerToken,
        brandToken,
        brand_order_token ?? "",
        phone_number ?? "",
        email ?? "",
        subject ?? "",
        description ?? "",
        details ?? "",
        userToken
      );

      if (!getSettingHelpCenterTicketPeteEventsFromBE()) {
        trackHelpCenterAllTypesOfTicketCreationSubmit(
          retailerToken,
          brandToken,
          brand_order_token ?? "",
          email ?? "",
          phone_number ?? "",
          subject ?? "",
          details ?? "",
          issueType,
          TicketType.PHONE,
          "",
          "",
          Strategy.SELF_SERVE,
          userToken
        );
      }
      setIsLoading(false);
      goToNextStep({ supportType: "phone" });
      setErrorMessage("");
    } catch (err) {
      setErrorMessage(
        strictLocalize({
          defaultMessage:
            "Something went wrong. Please try again. Or contact us via email.",
          description: {
            text: "Error message to inform the user that their request failed.",
          },
        })
      );
      setIsLoading(false);
    }
  };

  return (
    <LimitedWidthWrapper>
      <BackButton />
      <Spacer height="4x" />
      <Typography variant="sectionHeaderSansMedium">
        <StrictLocalMsg
          defaultMessage="Request a call"
          description={{
            text: "Title for requesting a call support",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography variant="paragraphSansRegular">
        <PhoneDescription formattedWaitTime={formattedWaitTime} />
      </Typography>
      <Spacer height="4x" />
      <InputWrapper>
        <PhoneInput
          data-test-id="call-request-phone-number-input"
          name="phoneNumber"
          label={
            <StrictLocalMsg
              defaultMessage="Account phone number"
              description={{
                text: "Field for the user to input their telephone number.",
              }}
            />
          }
          defaultPhoneNumber={phoneNumber}
          error={hasPhoneInputError ? <PhoneNumberErrorMessage /> : undefined}
          disabled
          sideCta={
            <Button
              variant="tertiary"
              linkTo={editSettingsLink}
              onClick={handleEditSettingsClick}
              target="_blank"
            >
              <StrictLocalMsg
                defaultMessage="Edit settings"
                description={{
                  text: "Button to redirect the user to the settings page to edit their phone number.",
                  stringDescriptors: [StringContextType.CALL_TO_ACTION],
                }}
              />
            </Button>
          }
        />
      </InputWrapper>
      <Spacer height="2x" />
      <InputWrapper>
        <TextArea
          style={{ height: "120px" }}
          label={strictLocalize({
            defaultMessage: "Issue details (optional)",
            description: {
              text: "Field for the user to input more details about their issue. And it's optional to fill in.",
              img: "https://cdn.faire.com/fastly/c5424ed6bb4ba9ce55b4ceb23874f006fdc2e6f250eba733bea6c3114b38fb52.png",
            },
          })}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
      </InputWrapper>
      {getSettingPhoneCallbackAttachment() ? (
        <>
          <Spacer height="2x" />
          <AttachmentFormField
            attachment={attachment}
            setAttachment={setAttachment}
          />
        </>
      ) : null}
      <Spacer height="3x" />
      <Button
        style={{ width: "160px", height: "56px" }}
        onClick={handleSubmit}
        data-test-id="request-call-btn"
      >
        <StrictLocalMsg
          defaultMessage="Request call"
          description={{
            text: "Button to request a call from Faire support.",
            img: "https://cdn.faire.com/fastly/c5424ed6bb4ba9ce55b4ceb23874f006fdc2e6f250eba733bea6c3114b38fb52.png",
            stringDescriptors: [StringContextType.CALL_TO_ACTION],
          }}
        />
      </Button>
      {errorMessage && !isLoading ? (
        <>
          <Spacer height="1/2x" />
          <Typography color={Core.text.critical}>{errorMessage}</Typography>
        </>
      ) : null}
      <Spacer height="2x" />
      <Typography variant="labelSansRegular" color={Core.text.subdued}>
        <StrictLocalMsg
          defaultMessage="By requesting a call, you agree to call recording which we use to improve our customer service."
          description={{
            img: "https://cdn.faire.com/fastly/c5424ed6bb4ba9ce55b4ceb23874f006fdc2e6f250eba733bea6c3114b38fb52.png",
          }}
        />
      </Typography>
    </LimitedWidthWrapper>
  );
};

const InputWrapper = styled.div`
  width: 100%;
`;
